import {useSendNotificationData} from '@/components/Drawer/hooks/useSendNotificationData'
import {InputWithCharLimit} from '@/components/SendNotification/InputWithCharLimit'
import {LandingPageSelector} from '@/components/SendNotification/LandingPageSelector'
import {AddLanguageButton, LanguageSelector} from '@/components/SendNotification/LanguageSelector'
import {RecipientDropDown} from '@/components/SendNotification/RecipientDropDown'
import RichTextEditor from '@/components/SendNotification/RichTextEditor'
import {FeatureToggle, isFeatureEnabled} from '@/helpers/featureToggle'
import {useJourneyMetaConfig} from '@/hooks/useJourneyMetaConfig'
import {NodeTypeEnum} from '@/types/NodeTypeEnum'
import {GetSendEmailStepConfigResponse} from '@/types/StepConfigParams'
import {StepSendEmailConfig} from '@/types/Steps'
import {FileUploadV2, Icon} from 'pepsico-ds'
import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'

interface SendEmailStepConfigParamsProps {
  getData: () => GetSendEmailStepConfigResponse
}

export default function SendEmailStepConfigParams({getData}: SendEmailStepConfigParamsProps): React.JSX.Element {
  const {formatMessage} = useIntl()
  const {isPublishedOrCompleted} = useJourneyMetaConfig()
  const [uploadedFileForMessage, setUploadedFileForMessage] = useState<File | null>(null)
  const [editorInstanceKey, setEditorInstanceKey] = useState(0)
  const {
    config: tempConfig,
    onChangeRecipient,
    onChangeSubject,
    onChangeMessage,
    onChangeLandingPage,
  } = useSendNotificationData(NodeTypeEnum.sendEmail)
  const config = tempConfig as StepSendEmailConfig

  const landingPageOptions = getData()?.landingPages ?? []
  const defaultLandingPage = landingPageOptions.find(option => option.isDefault)

  const handleFileUpload = async (file: File) => {
    try {
      // Read file content
      const content = await file.text()

      // Send the raw file content as the message; the editor is disabled while a file is uploaded
      onChangeMessage(content)
      setUploadedFileForMessage(file)
      setEditorInstanceKey(previousKey => previousKey + 1)
    } catch (error) {
      handleFileError(`Unable to read file. Make sure it's a text-based file (txt, eml, etc.)`)
      setUploadedFileForMessage(null)
    }
  }

  const handleRemoveUploadedFile = () => {
    onChangeMessage('')
    setUploadedFileForMessage(null)
    setEditorInstanceKey(previousKey => previousKey + 1)
  }

  const handleFileError = (error: string) => {
    console.error('File error:', error)
  }

  useEffect(() => {
    if (!config?.landing_page && defaultLandingPage) {
      onChangeLandingPage(defaultLandingPage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLandingPage])

  return (
    <div className="flex w-full max-w-5xl flex-col gap-4" data-testid="send-email-step-config-params">
      <div className="max-w-xs">
        <RecipientDropDown
          value={config?.to}
          label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.RECIPIENT_LABEL'})}
          onChange={onChangeRecipient}
          required
        />
      </div>
      <div className="max-w-xs">
        <InputWithCharLimit
          id="send-notification-subject"
          // charLimitSize={MAX_SUBJECT_LENGTH}
          inputValue={config?.subject}
          placeholder={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SUBJECT_PLACEHOLDER'})}
          onChangeInput={onChangeSubject}
          displayCharLimit={false}
          inputDisabled={isPublishedOrCompleted}
          label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SUBJECT_LABEL'})}
          required
        />
      </div>
      <div className="max-w-xs">
        <LanguageSelector label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.LANGUAGE_LABEL'})} />
      </div>

      <div className="flex gap-10">
        <div className="min-w-md flex-1">
          <RichTextEditor
            key={`${editorInstanceKey}-${isPublishedOrCompleted}`}
            value={config?.message}
            onChange={onChangeMessage}
            label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.MESSAGE_LABEL'})}
            required
            disabled={isPublishedOrCompleted || !!uploadedFileForMessage}
          />
        </div>
        {isFeatureEnabled(FeatureToggle.stepSendEmailTemplateUpload) && (
          <div className="w-full max-w-xs shrink-0">
            <span className="text-xs text-text-secondary">Email Template Upload</span>
            <FileUploadV2
              className="send-email-upload"
              data-testid="send-notification-file-input"
              buttonLabel={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.UPLOAD_FILE'})}
              onUpdate={handleFileUpload}
              description={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.UPLOAD_FILE_DESCRIPTION'})}
              onFileError={handleFileError}
              onValidateFile={file => {
                // Validate file size (max 5MB)
                return file.size <= 5 * 1024 * 1024
              }}
            />
            {uploadedFileForMessage && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <Icon icon="check" size="small" />
                <span>{uploadedFileForMessage.name}</span>
                {!isPublishedOrCompleted && (
                  <button
                    type="button"
                    onClick={handleRemoveUploadedFile}
                    aria-label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.REMOVE_UPLOADED_FILE'})}
                    data-testid="send-notification-remove-file-button"
                  >
                    <Icon icon="close" size="small" className="text-icon hover:text-button-icon-active" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="max-w-xs">
        <LandingPageSelector
          label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SELECT_LANDING_PAGE'})}
          options={landingPageOptions}
          value={config?.landing_page || defaultLandingPage}
          onChange={onChangeLandingPage}
          disabled={isPublishedOrCompleted}
        />
      </div>
      <div className="max-w-xs">
        <AddLanguageButton data-testid="add-aditional-language-button" />
      </div>
    </div>
  )
}
